import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { api } from "../../lib/api";
import { toast } from "sonner";
import {
  NotebookPen, Plus, Trash2, Pin, PinOff, Mic, Square,
  Sparkles, BookOpen, Send, Clock, Search, FolderPlus,
  CheckCircle2, Loader2, ExternalLink, Save, X, Crown,
} from "lucide-react";

/* ──────────────────────────────────────────────────────────────────
   Pro Note Taker — Faitharama's $79 Unlimited members get a
   markdown notepad with:
     • Folders + pinning + search
     • Auto-save every 1.5s (debounced) with version history
     • AI Writer AI (Expand / Improve / Summarize) via Claude
     • Voice-to-text (browser MediaRecorder → Whisper)
     • Bible verse lookup (bible-api proxy)
     • One-click "Publish as Article" → public SEO blog post
   Backed by /api/notes/* endpoints; gated server-side by tier=kingdom.
   ────────────────────────────────────────────────────────────────── */

const WINE   = "#0F172A";
const GOLD   = "#F39A1F";
const G      = "#2E9F3A";
const TEAL   = "#287F8E";
const NIGHT  = "#0F1116";

export default function NotesTab() {
  const [notes, setNotes]       = useState([]);
  const [folders, setFolders]   = useState(["Inbox"]);
  const [activeId, setActiveId] = useState(null);
  const [active, setActive]     = useState(null);            // currently-loaded note (full body)
  const [query, setQuery]       = useState("");
  const [filterFolder, setFilterFolder] = useState("All");
  const [saving, setSaving]     = useState(false);
  const [lastSaved, setLastSaved] = useState(null);          // ISO
  const [history, setHistory]   = useState([]);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [aiOpen, setAiOpen]     = useState(false);
  const [aiBusy, setAiBusy]     = useState(false);
  const [aiPreview, setAiPreview] = useState(null);          // { mode, result }
  const [biblePanel, setBiblePanel] = useState(false);
  const [bibleRef, setBibleRef] = useState("");
  const [bibleResult, setBibleResult] = useState(null);
  const [bibleBusy, setBibleBusy] = useState(false);
  const [recording, setRecording] = useState(false);
  const [recBusy, setRecBusy]   = useState(false);
  const [publishOpen, setPublishOpen] = useState(false);
  const [publishMeta, setPublishMeta] = useState({ description: "", keywords: "" });
  const [pubBusy, setPubBusy] = useState(false);
  const [proGate, setProGate] = useState(false);             // shown if 402

  const mediaRef = useRef(null);
  const chunksRef = useRef([]);
  const saveTimer = useRef(null);
  const lastBodyRef = useRef("");
  const lastTitleRef = useRef("");

  /* ─── Initial load ─── */
  const loadList = useCallback(async () => {
    try {
      const r = await api.get("/notes");
      setNotes(r.data.notes || []);
      setFolders(r.data.folders || ["Inbox"]);
      if (!activeId && r.data.notes?.length) {
        setActiveId(r.data.notes[0].id);
      }
    } catch (err) {
      if (err.response?.status === 402) setProGate(true);
      else toast.error("Could not load notes");
    }
  }, [activeId]);

  useEffect(() => { loadList(); }, [loadList]);

  /* ─── Load active note's full body when selected ─── */
  useEffect(() => {
    if (!activeId) { setActive(null); return; }
    api.get(`/notes/${activeId}`).then(r => {
      setActive(r.data);
      lastBodyRef.current = r.data.body || "";
      lastTitleRef.current = r.data.title || "";
      setLastSaved(r.data.updated_at);
    }).catch(() => toast.error("Could not load note"));
  }, [activeId]);

  /* ─── Debounced auto-save ─── */
  const scheduleSave = useCallback((next) => {
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(async () => {
      if (!next?.id) return;
      setSaving(true);
      try {
        const r = await api.put(`/notes/${next.id}`, {
          title:  next.title  ?? "",
          body:   next.body   ?? "",
          folder: next.folder ?? "Inbox",
          pinned: !!next.pinned,
          tags:   next.tags || [],
        });
        setLastSaved(r.data.updated_at);
        lastBodyRef.current  = next.body  ?? "";
        lastTitleRef.current = next.title ?? "";
        // Refresh the sidebar list silently so previews update
        loadList();
      } catch { toast.error("Auto-save hiccup — keep typing, we'll retry"); }
      finally { setSaving(false); }
    }, 1500);
  }, [loadList]);

  const onFieldChange = (field, value) => {
    if (!active) return;
    const next = { ...active, [field]: value };
    setActive(next);
    scheduleSave(next);
  };

  const onCreate = async () => {
    try {
      const r = await api.post("/notes", { title: "Untitled note", body: "", folder: "Inbox" });
      const newNote = r.data.note;
      setNotes((n) => [newNote, ...n]);
      setActiveId(newNote.id);
      toast.success("Note created");
    } catch (err) {
      if (err.response?.status === 402) setProGate(true);
      else toast.error("Could not create note");
    }
  };

  const onDelete = async () => {
    if (!active) return;
    if (!confirm(`Delete "${active.title}" forever? This can't be undone.`)) return;
    try {
      await api.delete(`/notes/${active.id}`);
      setNotes(notes.filter(n => n.id !== active.id));
      setActiveId(notes.find(n => n.id !== active.id)?.id || null);
      setActive(null);
      toast.success("Note deleted");
    } catch { toast.error("Could not delete"); }
  };

  const onTogglePin = async () => {
    if (!active) return;
    const next = { ...active, pinned: !active.pinned };
    setActive(next);
    scheduleSave(next);
  };

  /* ─── AI menu ─── */
  const runAI = async (mode) => {
    if (!active) return;
    setAiBusy(true); setAiOpen(false);
    try {
      const r = await api.post(`/notes/${active.id}/ai/${mode}`, { body: active.body || "" });
      setAiPreview({ mode, result: r.data.result });
    } catch (err) {
      toast.error(err.response?.data?.detail || "AI Writer hiccup");
    } finally { setAiBusy(false); }
  };

  const acceptAI = () => {
    if (!aiPreview) return;
    const next = { ...active, body: aiPreview.result };
    setActive(next);
    scheduleSave(next);
    setAiPreview(null);
    toast.success("AI suggestion applied");
  };

  /* ─── Voice-to-text ─── */
  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const rec = new MediaRecorder(stream, { mimeType: "audio/webm" });
      chunksRef.current = [];
      rec.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      rec.onstop = async () => {
        stream.getTracks().forEach(t => t.stop());
        const blob = new Blob(chunksRef.current, { type: "audio/webm" });
        await transcribeBlob(blob);
      };
      rec.start();
      mediaRef.current = rec;
      setRecording(true);
    } catch (err) {
      toast.error("Microphone permission denied");
    }
  };

  const stopRecording = () => {
    if (mediaRef.current && mediaRef.current.state !== "inactive") {
      mediaRef.current.stop();
    }
    setRecording(false);
  };

  const transcribeBlob = async (blob) => {
    setRecBusy(true);
    try {
      const fd = new FormData();
      fd.append("audio", blob, "voice.webm");
      const r = await api.post("/notes/transcribe", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 60000,
      });
      const txt = (r.data.text || "").trim();
      if (txt && active) {
        const sep = (active.body && !active.body.endsWith("\n")) ? "\n\n" : "";
        const next = { ...active, body: (active.body || "") + sep + txt };
        setActive(next);
        scheduleSave(next);
        toast.success("Voice note added");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Voice transcription failed");
    } finally { setRecBusy(false); }
  };

  /* ─── Bible verse lookup ─── */
  const lookupBible = async () => {
    if (!bibleRef.trim()) return;
    setBibleBusy(true); setBibleResult(null);
    try {
      const r = await api.post("/notes/bible/lookup", { reference: bibleRef.trim(), translation: "kjv" });
      setBibleResult(r.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Lookup failed");
    } finally { setBibleBusy(false); }
  };

  const insertVerse = () => {
    if (!bibleResult || !active) return;
    const block = `\n\n> "${bibleResult.text.trim()}"\n> — ${bibleResult.reference} (${bibleResult.translation})\n\n`;
    const next = { ...active, body: (active.body || "") + block };
    setActive(next);
    scheduleSave(next);
    setBiblePanel(false);
    setBibleRef("");
    setBibleResult(null);
    toast.success("Verse inserted");
  };

  /* ─── History ─── */
  const openHistory = async () => {
    if (!active) return;
    setHistoryOpen(true);
    try {
      const r = await api.get(`/notes/${active.id}/history`);
      setHistory(r.data.versions || []);
    } catch { toast.error("Could not load history"); }
  };

  const restoreVersion = async (version) => {
    if (!active) return;
    if (!confirm(`Roll back to version ${version}? Your current draft is saved first.`)) return;
    try {
      await api.post(`/notes/${active.id}/restore/${version}`);
      const r = await api.get(`/notes/${active.id}`);
      setActive(r.data);
      lastBodyRef.current = r.data.body || "";
      lastTitleRef.current = r.data.title || "";
      setHistoryOpen(false);
      toast.success(`Restored to version ${version}`);
      loadList();
    } catch { toast.error("Restore failed"); }
  };

  /* ─── Publish to Article ─── */
  const openPublish = () => {
    if (!active) return;
    setPublishMeta({
      description: (active.body || "").slice(0, 160).replace(/\n+/g, " "),
      keywords: "",
    });
    setPublishOpen(true);
  };

  const doPublish = async () => {
    if (!active) return;
    setPubBusy(true);
    try {
      const r = await api.post(`/notes/${active.id}/publish`, {
        description: publishMeta.description,
        keywords: publishMeta.keywords,
        publish: true,
      });
      toast.success("Published!");
      setPublishOpen(false);
      window.open(r.data.public_url, "_blank");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not publish");
    } finally { setPubBusy(false); }
  };

  /* ─── Filtered note list ─── */
  const filtered = useMemo(() => {
    let arr = notes;
    if (filterFolder !== "All") arr = arr.filter(n => (n.folder || "Inbox") === filterFolder);
    if (query.trim()) {
      const q = query.toLowerCase();
      arr = arr.filter(n => (n.title + " " + (n.preview || "")).toLowerCase().includes(q));
    }
    return arr;
  }, [notes, query, filterFolder]);

  /* ────────────────────────────── PRO GATE ────────────────────────────── */
  if (proGate) {
    return (
      <div className="space-y-6" data-testid="notes-pro-gate">
        <div className="rounded-2xl p-8 md:p-12 text-center shadow-2xl"
             style={{ background: `linear-gradient(135deg, ${WINE} 0%, #7C2A2A 100%)`, color: "#FFFFFF" }}>
          <Crown className="w-12 h-12 mx-auto" style={{ color: GOLD }}/>
          <h2 className="font-serif-display text-3xl md:text-5xl uppercase mt-3" style={{ color: GOLD, textWrap: "balance" }}>
            Pro Note Taker
          </h2>
          <p className="mt-4 text-lg leading-relaxed max-w-xl mx-auto">
            Voice-to-text, AI Writer AI, Bible verse lookup, daily scripture stamp —
            all built into one notepad that syncs to every device you log in on.
            Available on <strong style={{ color: GOLD }}>Unlimited ($79/mo)</strong>.
          </p>
          <Link to="/pricing" data-testid="notes-upgrade-btn"
                className="inline-flex items-center gap-2 mt-6 px-6 py-4 rounded-md text-base font-bold uppercase tracking-[0.16em] shadow-2xl hover:scale-[1.03] transition"
                style={{ background: GOLD, color: WINE }}>
            Upgrade To Pro
          </Link>
        </div>
      </div>
    );
  }

  /* ────────────────────────────── MAIN UI ────────────────────────────── */
  return (
    <div className="space-y-4" data-testid="notes-tab">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs tracking-wider-cap text-gold-deep">Pro Workspace</div>
          <h2 className="font-serif-display text-3xl text-red-wine">Your Notes</h2>
        </div>
        <button onClick={onCreate} data-testid="notes-new"
                className="inline-flex items-center gap-2 px-5 py-3 rounded-md text-sm font-bold uppercase tracking-[0.18em] text-white shadow hover:scale-[1.02] transition"
                style={{ background: G }}>
          <Plus className="w-4 h-4"/> New Note
        </button>
      </div>

      <div className="grid md:grid-cols-[320px_1fr] gap-4 min-h-[600px]">
        {/* ─── SIDEBAR ─── */}
        <aside className="rounded-2xl bg-white border-2 shadow-sm overflow-hidden flex flex-col"
               style={{ borderColor: "#E5DCC4" }} data-testid="notes-sidebar">
          {/* Search */}
          <div className="p-3 border-b border-[#E5DCC4]">
            <div className="relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-foreground/50"/>
              <input value={query} onChange={e => setQuery(e.target.value)}
                     placeholder="Search notes…"
                     data-testid="notes-search"
                     className="w-full pl-9 pr-3 py-2.5 text-sm rounded-md border border-border bg-white"/>
            </div>
            {/* Folder pills */}
            <div className="flex flex-wrap gap-1.5 mt-3">
              {["All", ...folders].map(f => (
                <button key={f} onClick={() => setFilterFolder(f)} data-testid={`folder-${f}`}
                        className={`px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-[0.16em] border transition ${
                          filterFolder === f
                            ? "bg-[#0F172A] text-white border-[#0F172A]"
                            : "bg-white text-foreground border-border hover:border-[#0F172A]/50"
                        }`}>
                  {f}
                </button>
              ))}
            </div>
          </div>
          {/* Note list */}
          <div className="flex-1 overflow-y-auto" data-testid="notes-list">
            {filtered.length === 0 && (
              <div className="p-6 text-center text-foreground/60 text-sm">
                {notes.length === 0 ? "Your first note awaits." : "No matches."}
              </div>
            )}
            {filtered.map(n => (
              <button key={n.id} onClick={() => setActiveId(n.id)} data-testid={`note-row-${n.id}`}
                      className={`block w-full text-left px-4 py-3 border-b border-[#E5DCC4]/60 transition ${
                        activeId === n.id ? "bg-white" : "hover:bg-white"
                      }`}>
                <div className="flex items-start gap-2">
                  {n.pinned && <Pin className="w-3.5 h-3.5 mt-1 shrink-0" style={{ color: GOLD }}/>}
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-bold truncate" style={{ color: "#0F172A" }}>
                      {n.title || "Untitled"}
                    </div>
                    <div className="text-[11px] text-foreground/60 line-clamp-2 mt-0.5">
                      {n.preview || "(empty)"}
                    </div>
                    <div className="text-[10px] tracking-wider-cap uppercase mt-1.5 text-foreground/50">
                      {(n.folder || "Inbox")} · v{n.version || 1}
                    </div>
                  </div>
                </div>
              </button>
            ))}
          </div>
        </aside>

        {/* ─── EDITOR ─── */}
        <main className="rounded-2xl bg-white border-2 shadow-sm overflow-hidden flex flex-col"
              style={{ borderColor: "#E5DCC4" }}>
          {!active ? (
            <div className="flex-1 flex flex-col items-center justify-center p-10 text-center text-foreground/60">
              <NotebookPen className="w-12 h-12 mb-3"/>
              <p className="text-base">Pick a note from the left — or hit <strong>New Note</strong>.</p>
            </div>
          ) : (
            <>
              {/* Toolbar */}
              <div className="flex flex-wrap items-center gap-2 p-3 border-b border-[#E5DCC4] bg-[#EAF6FC]"
                   data-testid="notes-toolbar">
                <input value={active.title || ""}
                       onChange={e => onFieldChange("title", e.target.value)}
                       placeholder="Title"
                       data-testid="note-title"
                       className="flex-1 min-w-[200px] px-3 py-2 text-lg font-serif-display rounded-md border border-border bg-white"
                       style={{ color: "#0F172A", fontWeight: 700 }}/>
                <select value={active.folder || "Inbox"}
                        onChange={e => onFieldChange("folder", e.target.value)}
                        data-testid="note-folder"
                        className="px-3 py-2 text-sm rounded-md border border-border bg-white">
                  {[...new Set([...folders, "Inbox", "Sermons", "Ministry", "Affiliate", "Personal"])].map(f => (
                    <option key={f} value={f}>{f}</option>
                  ))}
                </select>
                <button onClick={onTogglePin} data-testid="note-pin"
                        className="p-2 rounded-md hover:bg-white"
                        title={active.pinned ? "Unpin" : "Pin to top"}>
                  {active.pinned
                    ? <PinOff className="w-4 h-4" style={{ color: GOLD }}/>
                    : <Pin    className="w-4 h-4 text-foreground/60"/>}
                </button>
                <button onClick={onDelete} data-testid="note-delete"
                        className="p-2 rounded-md hover:bg-red-50" title="Delete note">
                  <Trash2 className="w-4 h-4 text-red-wine"/>
                </button>
              </div>

              {/* Power-tools toolbar — Voice / AI / Bible / History / Publish */}
              <div className="flex flex-wrap items-center gap-2 px-3 py-2 border-b border-[#E5DCC4] bg-white"
                   data-testid="notes-power-toolbar">
                {/* Voice */}
                {!recording ? (
                  <button onClick={startRecording} disabled={recBusy} data-testid="note-voice"
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[11px] font-bold uppercase tracking-[0.18em] border-2 hover:bg-white transition disabled:opacity-50"
                          style={{ borderColor: TEAL, color: TEAL }}>
                    {recBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin"/> : <Mic className="w-3.5 h-3.5"/>}
                    {recBusy ? "Transcribing…" : "Voice → Text"}
                  </button>
                ) : (
                  <button onClick={stopRecording} data-testid="note-voice-stop"
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[11px] font-bold uppercase tracking-[0.18em] text-white shadow animate-pulse"
                          style={{ background: "#C42B3F" }}>
                    <Square className="w-3.5 h-3.5"/> Stop Recording
                  </button>
                )}

                {/* AI menu */}
                <div className="relative">
                  <button onClick={() => setAiOpen(!aiOpen)} disabled={aiBusy} data-testid="note-ai-btn"
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[11px] font-bold uppercase tracking-[0.18em] text-white shadow hover:scale-[1.02] transition disabled:opacity-50"
                          style={{ background: WINE }}>
                    {aiBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin"/> : <Sparkles className="w-3.5 h-3.5"/>}
                    AI Writer AI
                  </button>
                  {aiOpen && (
                    <div className="absolute z-30 mt-1 bg-white rounded-md shadow-2xl border border-border min-w-[200px]"
                         data-testid="note-ai-menu">
                      <button onClick={() => runAI("expand")} className="block w-full text-left px-4 py-3 text-sm hover:bg-white border-b border-border"
                              data-testid="ai-expand">
                        <strong className="block">★ Expand</strong>
                        <span className="text-[11px] text-foreground/60">Bullets → 3 paragraphs</span>
                      </button>
                      <button onClick={() => runAI("improve")} className="block w-full text-left px-4 py-3 text-sm hover:bg-white border-b border-border"
                              data-testid="ai-improve">
                        <strong className="block">★ Improve</strong>
                        <span className="text-[11px] text-foreground/60">Tighten and clarify</span>
                      </button>
                      <button onClick={() => runAI("summarize")} className="block w-full text-left px-4 py-3 text-sm hover:bg-white"
                              data-testid="ai-summarize">
                        <strong className="block">★ Summarize</strong>
                        <span className="text-[11px] text-foreground/60">3-5 bullet points</span>
                      </button>
                    </div>
                  )}
                </div>

                {/* Bible */}
                <button onClick={() => setBiblePanel(!biblePanel)} data-testid="note-bible-btn"
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[11px] font-bold uppercase tracking-[0.18em] border-2 hover:bg-white transition"
                        style={{ borderColor: G, color: G }}>
                  <BookOpen className="w-3.5 h-3.5"/> Bible Verse
                </button>

                {/* History */}
                <button onClick={openHistory} data-testid="note-history-btn"
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[11px] font-bold uppercase tracking-[0.18em] border-2 hover:bg-white transition"
                        style={{ borderColor: "#5C5249", color: "#5C5249" }}>
                  <Clock className="w-3.5 h-3.5"/> History
                </button>

                {/* Publish */}
                <button onClick={openPublish} data-testid="note-publish-btn"
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[11px] font-bold uppercase tracking-[0.18em] text-white shadow hover:scale-[1.02] transition"
                        style={{ background: GOLD }}>
                  <Send className="w-3.5 h-3.5"/> Publish As Article
                </button>

                {/* Save status */}
                <div className="ml-auto text-[10px] tracking-wider-cap uppercase text-foreground/60 inline-flex items-center gap-1"
                     data-testid="note-save-status">
                  {saving
                    ? <><Loader2 className="w-3 h-3 animate-spin"/> Saving…</>
                    : <><Save className="w-3 h-3"/> Saved {lastSaved ? new Date(lastSaved).toLocaleTimeString() : ""}</>}
                </div>
              </div>

              {/* Bible side-panel (inline, dismissible) */}
              {biblePanel && (
                <div className="px-4 py-3 border-b border-[#E5DCC4] bg-[#F2F8F2]" data-testid="bible-panel">
                  <div className="flex items-center gap-2">
                    <BookOpen className="w-4 h-4" style={{ color: G }}/>
                    <input value={bibleRef} onChange={e => setBibleRef(e.target.value)}
                           onKeyDown={e => e.key === "Enter" && lookupBible()}
                           placeholder="e.g. John 3:16 or Romans 8:28-30"
                           data-testid="bible-input"
                           className="flex-1 px-3 py-2 text-sm rounded-md border border-border bg-white"/>
                    <button onClick={lookupBible} disabled={bibleBusy} data-testid="bible-lookup"
                            className="px-3 py-2 text-[11px] font-bold uppercase tracking-[0.16em] rounded-md text-white shadow disabled:opacity-50"
                            style={{ background: G }}>
                      {bibleBusy ? "…" : "Look up"}
                    </button>
                    <button onClick={() => setBiblePanel(false)} className="p-2 rounded-md hover:bg-white">
                      <X className="w-4 h-4 text-foreground/60"/>
                    </button>
                  </div>
                  {bibleResult && (
                    <div className="mt-3 p-3 bg-white rounded-md border-l-4" style={{ borderColor: G }}
                         data-testid="bible-result">
                      <p className="text-base italic leading-relaxed" style={{ color: "#0F172A" }}>
                        "{bibleResult.text}"
                      </p>
                      <p className="text-xs mt-2 text-foreground/70">
                        — {bibleResult.reference} ({bibleResult.translation})
                      </p>
                      <button onClick={insertVerse} data-testid="bible-insert"
                              className="mt-2 inline-flex items-center gap-1 px-3 py-1.5 text-[11px] font-bold uppercase tracking-[0.16em] rounded-md text-white"
                              style={{ background: G }}>
                        Insert Into Note
                      </button>
                    </div>
                  )}
                </div>
              )}

              {/* Body editor */}
              <textarea value={active.body || ""}
                        onChange={e => onFieldChange("body", e.target.value)}
                        placeholder="Start typing… or hit the mic and just talk."
                        data-testid="note-body"
                        className="flex-1 w-full px-5 py-4 text-lg leading-relaxed font-mono resize-none outline-none"
                        style={{ minHeight: 400, color: "#0F172A", background: "#FFFFFF" }}/>

              {/* AI preview overlay */}
              {aiPreview && (
                <div className="border-t-2 bg-white p-4" style={{ borderColor: GOLD }}
                     data-testid="ai-preview">
                  <div className="text-[10px] tracking-[0.32em] uppercase font-bold mb-2" style={{ color: WINE }}>
                    ★ AI WRITER · {aiPreview.mode.toUpperCase()}
                  </div>
                  <pre className="whitespace-pre-wrap text-base leading-relaxed font-sans" style={{ color: "#0F172A" }}>
                    {aiPreview.result}
                  </pre>
                  <div className="flex gap-2 mt-3">
                    <button onClick={acceptAI} data-testid="ai-accept"
                            className="inline-flex items-center gap-1 px-4 py-2 text-[11px] font-bold uppercase tracking-[0.18em] rounded-md text-white shadow"
                            style={{ background: G }}>
                      <CheckCircle2 className="w-4 h-4"/> Replace Note
                    </button>
                    <button onClick={() => setAiPreview(null)} data-testid="ai-discard"
                            className="px-4 py-2 text-[11px] font-bold uppercase tracking-[0.18em] rounded-md border border-border hover:bg-white">
                      Discard
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </main>
      </div>

      {/* ─── HISTORY MODAL ─── */}
      {historyOpen && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4"
             onClick={() => setHistoryOpen(false)}>
          <div onClick={e => e.stopPropagation()}
               className="bg-white rounded-2xl max-w-2xl w-full max-h-[80vh] overflow-hidden flex flex-col shadow-2xl"
               data-testid="history-modal">
            <div className="p-5 border-b border-border flex items-center justify-between">
              <h3 className="font-serif-display text-2xl text-red-wine">Version History</h3>
              <button onClick={() => setHistoryOpen(false)} className="p-1 hover:bg-white rounded">
                <X className="w-5 h-5"/>
              </button>
            </div>
            <div className="overflow-y-auto p-4">
              {history.length === 0 ? (
                <p className="text-center text-foreground/60 py-8">No saved versions yet — make a change and we'll start tracking.</p>
              ) : history.map(v => (
                <div key={v.id} className="p-3 border-b border-border" data-testid={`history-v-${v.version}`}>
                  <div className="flex items-center justify-between">
                    <div className="text-sm font-bold">Version {v.version} · {v.title}</div>
                    <button onClick={() => restoreVersion(v.version)}
                            className="text-[11px] font-bold uppercase tracking-[0.16em] text-red-wine hover:underline">
                      Restore
                    </button>
                  </div>
                  <div className="text-[11px] text-foreground/60 mt-0.5">
                    {new Date(v.created_at).toLocaleString()}
                  </div>
                  <div className="text-sm text-foreground/80 mt-1 italic">"{v.preview}"</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ─── PUBLISH MODAL ─── */}
      {publishOpen && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4"
             onClick={() => setPublishOpen(false)}>
          <div onClick={e => e.stopPropagation()}
               className="bg-white rounded-2xl max-w-lg w-full overflow-hidden shadow-2xl"
               data-testid="publish-modal">
            <div className="p-5 border-b border-border">
              <h3 className="font-serif-display text-2xl text-red-wine">Publish As Article</h3>
              <p className="text-sm text-foreground/70 mt-1">
                Your note becomes a public article at <code className="text-xs">/articles/{`<slug>`}</code> — indexed by Google with full SEO meta.
              </p>
            </div>
            <div className="p-5 space-y-3">
              <div>
                <label className="text-xs tracking-wider-cap font-bold text-foreground/80">SEO description (160 chars)</label>
                <textarea rows="2" maxLength="200"
                          value={publishMeta.description}
                          onChange={e => setPublishMeta({ ...publishMeta, description: e.target.value })}
                          data-testid="publish-description"
                          className="w-full mt-1 px-3 py-2 text-sm rounded-md border border-border bg-white"/>
              </div>
              <div>
                <label className="text-xs tracking-wider-cap font-bold text-foreground/80">Keywords (comma separated)</label>
                <input value={publishMeta.keywords}
                       onChange={e => setPublishMeta({ ...publishMeta, keywords: e.target.value })}
                       placeholder="christian wellness, AI Writer, daily devotional"
                       data-testid="publish-keywords"
                       className="w-full mt-1 px-3 py-2 text-sm rounded-md border border-border bg-white"/>
              </div>
            </div>
            <div className="p-5 border-t border-border flex justify-end gap-2">
              <button onClick={() => setPublishOpen(false)}
                      className="px-4 py-2 text-sm font-bold uppercase tracking-[0.16em] rounded-md border border-border hover:bg-white">
                Cancel
              </button>
              <button onClick={doPublish} disabled={pubBusy} data-testid="publish-confirm"
                      className="inline-flex items-center gap-2 px-4 py-2 text-sm font-bold uppercase tracking-[0.16em] rounded-md text-white shadow disabled:opacity-50"
                      style={{ background: GOLD }}>
                {pubBusy ? <Loader2 className="w-4 h-4 animate-spin"/> : <ExternalLink className="w-4 h-4"/>}
                Publish
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
